<template>
    <div>
        <div
            v-for="(group, index) in modifierGroups"
            :id="`${group.id}-${index}`"
            :key="`${group.id}-${index}`"
            :class="{
                hidden: group.isHidden
            }"
        >
            <div
                v-if="group?.products?.length"
                class="px-6 py-3.5 bg-page sm:sticky sm:top-0 z-40"
                :ref="`modifier-group-${index}`"
                :class="{
                    'cursor-pointer':
                        group.initialDisplaySetting ===
                        initialDisplaySettingEnum.COLLAPSED,
                    hidden:
                        group.initialDisplaySetting ===
                        initialDisplaySettingEnum.HIDDEN
                }"
                @click="toggleAccordion($event, $refs[group.id])"
            >
                <span
                    v-if="
                        $v.modifierGroups.$each[index] &&
                        $v.modifierGroups.$each[index].$error
                    "
                    id="modifier-error"
                />

                <div
                    class="flex flex-row select-none items-start justify-between pointer-events-none"
                >
                    <div>
                        <h5
                            :class="{
                                'error-text':
                                    $v.modifierGroups.$each[index].$error
                            }"
                            class="text-subheading text-sm font-bold"
                        >
                            {{ group.name }}
                        </h5>
                        <div
                            v-if="group.description"
                            class="text-itembody text-xs"
                        >
                            {{ group.description }}
                        </div>
                        <div
                            v-if="group.freeModifiers > 0"
                            class="text-itembody text-xs"
                        >
                            {{ freeModifiersMessage(group) }}
                        </div>
                        <div v-if="!(group.min === 1 && group.max === 1)">
                            <h6
                                v-if="group.max === 1"
                                class="text-itembody text-xs"
                            >
                                {{ $t('itemModal.selectOne') }}
                            </h6>
                            <h6
                                v-else-if="
                                    group.min > 0 && group.min === group.max
                                "
                                class="text-itembody text-xs"
                            >
                                {{
                                    $t('itemModal.selectMin', {
                                        value: group.min
                                    })
                                }}
                            </h6>
                            <h6
                                v-else-if="group.max !== 0"
                                class="text-itembody text-xs"
                            >
                                {{
                                    $t('itemModal.selectMax', {
                                        value: group.max
                                    })
                                }}
                            </h6>
                        </div>
                    </div>
                    <div class="flex text-itembody">
                        <div
                            v-if="
                                !visualMenu &&
                                !allergenDisabled &&
                                group.min > 0
                            "
                            class="flex items-center pl-4 text-sm font-medium"
                            style="color: rgb(57, 178, 56)"
                        >
                            <check-icon
                                v-if="group.selectedOption >= group.min"
                            />
                            <span>
                                {{ $t('itemModal.required') }}
                            </span>
                        </div>
                        <div
                            v-if="
                                group.initialDisplaySetting ===
                                initialDisplaySettingEnum.COLLAPSED
                            "
                            :class="{
                                'rotate-90':
                                    group.initialDisplaySetting ===
                                    initialDisplaySettingEnum.COLLAPSED
                            }"
                            class="caret text-secondaryactions transform transition duration-300 ease-in-out"
                        >
                            <dropdown-icon class="pointer-events-none" />
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="group.products?.length"
                :ref="group.id"
                :class="{
                    'accordion collapsed':
                        group.initialDisplaySetting ===
                        initialDisplaySettingEnum.COLLAPSED
                }"
                class="px-6 overflow-hidden transition transition-height duration-300 ease-in-out"
            >
                <radio-button
                    v-if="group.min === 1 && group.max === 1"
                    v-model="modifierGroups[index].selectedOption"
                    :item-price="item.price"
                    :keyId="`${group.id}-${index}`"
                    :multi-max="group.multiMax"
                    :options="modifierGroups[index].products"
                    :show-prices="shouldShowPrices(group)"
                    :paid-option-ids="paidOptionIds(group)"
                    class="my-2.5"
                    full-width
                    label-field="name"
                    value-field="id"
                    @input="onChange(index, 'radio')"
                />
                <checkbox
                    v-else
                    v-model="modifierGroups[index].selectedOptions"
                    :item-price="item.price"
                    :max="group.max"
                    :multi-max="group.multiMax"
                    :optionGroupId="`${group.id}-${index}`"
                    :options="modifierGroups[index].products"
                    :show-prices="shouldShowPrices(group)"
                    :paid-option-ids="paidOptionIds(group)"
                    class="my-2.5"
                    labelField="name"
                    valueField="id"
                    @input="onChange(index, 'checkbox')"
                />
            </div>
        </div>
    </div>
</template>

<script>
import RadioButton from '@/components/formElements/RadioButton.vue';
import DropdownIcon from '@/components/icons/DropdownIcon.vue';
import Checkbox from '@/components/formElements/Checkbox.vue';
import { requiredIf } from 'vuelidate/lib/validators';
import { mapGetters, mapState } from 'vuex';
import toggleAccordion from '@/helpers/accordion';
import CheckIcon from '@/components/icons/CheckIcon.vue';
import { getPaidOptionIds } from '@/helpers/freeModifiersPricing';

const initialDisplaySettingEnum = Object.freeze({
    COLLAPSED: 'collapsed',
    EXPANDED: 'expanded',
    HIDDEN: 'hidden'
});

export default {
    name: 'ItemModifiers',
    inject: ['registerValidator'],
    props: {
        item: {
            type: Object,
            required: true
        },
        value: {
            type: Array,
            required: true
        }
    },
    validations: {
        modifierGroups: {
            $each: {
                selectedOption: {
                    required: requiredIf(function (group) {
                        if (group.isHidden) {
                            return false;
                        }

                        if (group.min === 0) {
                            return false;
                        }

                        if (group.max === 1) {
                            return !group.selectedOption;
                        }

                        const selections = group.selectedOptions || [];

                        const totalQuantity = selections.reduce(
                            (acc, opt) => acc + (opt?.quantity ?? 1),
                            0
                        );

                        const effectiveMax =
                            group.max || Math.max(group.multiMax, 99);

                        return (
                            totalQuantity < group.min ||
                            totalQuantity > effectiveMax
                        );
                    })
                }
            }
        }
    },
    components: {
        CheckIcon,
        Checkbox,
        RadioButton,
        DropdownIcon
    },
    mounted() {
        this.registerValidator(this.validate);
        this.preselectModifierOptions();
        this.updateNestedGroups();
    },
    computed: {
        initialDisplaySettingEnum: () => initialDisplaySettingEnum,
        ...mapState({
            visualMenu: state => state.venue.venue.visualMenu
        }),
        ...mapGetters({
            allergenDisabled: 'customer/allergenDisabled'
        }),
        modifierGroups: {
            get() {
                return this.value;
            },
            set(newValue) {
                this.$emit('input', newValue);
            }
        },
        groupsById() {
            return this.modifierGroups.reduce((map, g) => {
                map[g.id] = g;
                return map;
            }, {});
        },
        activeNested() {
            return this.modifierGroups.reduce((set, group) => {
                const selected =
                    group.min === 1 && group.max === 1
                        ? [group.selectedOption]
                        : group.selectedOptions || [];

                selected.forEach(opt => {
                    opt?.subModifierGroups?.forEach?.(id => set.add(id));
                });

                return set;
            }, new Set());
        }
    },
    methods: {
        toggleAccordion,
        onChange(index, type) {
            if (type) this.scrollToNext(index, type);
            this.updateNestedGroups();
        },
        updateNestedGroups() {
            const activeNested = this.activeNested;
            const visited = new Set();

            const traverse = id => {
                const grp = this.groupsById[id];
                if (!grp || visited.has(id)) return;
                visited.add(id);

                const isActive = activeNested.has(id);

                if (grp.isNested) {
                    this.$set(grp, 'isHidden', !isActive);
                    if (!isActive) {
                        if (grp.max === 1) {
                            if (grp.selectedOption !== null)
                                this.$set(grp, 'selectedOption', null);
                        } else if (grp.selectedOptions?.length) {
                            this.$set(grp, 'selectedOptions', []);
                        }
                    }
                }

                grp.nestedGroups?.forEach(traverse);
            };

            this.modifierGroups.forEach(group => traverse(group.id));
        },
        validate() {
            this.$v.$touch();

            if (!this.$v.$invalid) {
                return true;
            }

            this.$nextTick(() => {
                if (!document.getElementById('modifier-error')) {
                    return;
                }

                this.scrollTo('modifier-error', 'start');
                this.animateErrorGroup();
            });

            return false;
        },
        scrollToNext(index, origin) {
            if (!~index) {
                return;
            }

            const isLastModifier = this.modifierGroups.length === index + 1;
            const nextItem = this.modifierGroups[index + 1];
            const isCurrentSelectionReached =
                ((this.modifierGroups[index].min !== 1 ||
                    this.modifierGroups[index].max !== 1) &&
                    this.modifierGroups[index].max ===
                        this.modifierGroups[index].selectedOptions?.length) ||
                (this.modifierGroups[index].max === 1 &&
                    !!this.modifierGroups[index].selectedOption);

            const isNextSelectionReached =
                (this.modifierGroups[index + 1]?.min !== 1 ||
                    this.modifierGroups[index + 1]?.max !== 1) &&
                this.modifierGroups[index + 1]?.max ===
                    this.modifierGroups[index + 1]?.selectedOptions?.length;

            if (!isCurrentSelectionReached && origin === 'checkbox') {
                return;
            }

            if (
                isLastModifier ||
                nextItem?.selectedOption ||
                (isCurrentSelectionReached && isNextSelectionReached)
            ) {
                const unselectedModifierIndex = this.modifierGroups.findIndex(
                    modifier =>
                        (!modifier.selectedOption &&
                            modifier.min === 1 &&
                            modifier.max === 1) ||
                        (!modifier.selectedOptions?.length &&
                            modifier.min > 0 &&
                            modifier.max > 1)
                );

                if (~unselectedModifierIndex) {
                    this.scrollTo(
                        `${this.modifierGroups[unselectedModifierIndex].id}-${unselectedModifierIndex}`
                    );
                }

                return;
            }

            this.scrollTo(`${nextItem.id}-${index + 1}`);
        },
        scrollTo(id, block = 'center') {
            const element = document.getElementById(id);

            if (!element) {
                return;
            }

            element.scrollIntoView({ behavior: 'smooth', block });
        },
        animateErrorGroup() {
            for (let index = 0; index < this.modifierGroups.length; index++) {
                if (this.$v.modifierGroups.$each[index]?.$error) {
                    const groupElement =
                        this.$refs[`modifier-group-${index}`][0];

                    if (groupElement) {
                        groupElement.classList.add('wobble');

                        setTimeout(() => {
                            groupElement.classList.remove('wobble');
                        }, 1600);
                    }

                    break;
                }
            }
        },
        preselectModifierOptions() {
            this.modifierGroups.forEach(modifierGroup => {
                // Align with template's condition for RadioButton vs Checkbox
                const usesRadioButton =
                    modifierGroup.min === 1 && modifierGroup.max === 1;

                const selections = modifierGroup.products
                    .filter(m => m.defaultQuantity)
                    .map(m => ({ id: m.id, quantity: m.defaultQuantity }));

                if (!selections.length) {
                    return;
                }

                if (usesRadioButton) {
                    // RadioButton expects selectedOption (object)
                    const last = selections.length
                        ? selections[selections.length - 1]
                        : null;
                    this.$set(modifierGroup, 'selectedOption', last);
                } else {
                    // Checkbox expects selectedOptions (array)
                    this.$set(modifierGroup, 'selectedOptions', selections);
                }
            });
        },
        shouldShowPrices(group) {
            const freeModifiers = Number(group.freeModifiers) || 0;

            return freeModifiers === 0;
        },
        totalSelectedCount(group) {
            if (group.max === 1) {
                return group.selectedOption
                    ? group.selectedOption.quantity || 1
                    : 0;
            }

            const selections = group.selectedOptions || [];

            return selections.reduce(
                (acc, opt) => acc + (opt?.quantity ?? 1),
                0
            );
        },
        remainingFreeCount(group) {
            const freeModifiers = Number(group.freeModifiers) || 0;
            const selected = this.totalSelectedCount(group);

            return Math.max(freeModifiers - selected, 0);
        },
        freeModifiersMessage(group) {
            const freeModifiers = Number(group.freeModifiers) || 0;

            if (freeModifiers <= 0) {
                return '';
            }

            const left = this.remainingFreeCount(group);

            if (freeModifiers === 1) {
                return `1 is included (left: ${left})`;
            }

            return `First ${freeModifiers} are included (left: ${left})`;
        },
        paidOptionIds(group) {
            return getPaidOptionIds(group);
        }
    }
};
</script>
